/**
 * Materials Airtable Integration
 * 
 * Provides Airtable operations for the CDN-first materials management system.
 * Integrates with the existing operationsBase and follows established patterns.
 */

import * as Sentry from '@sentry/react';
import { operationsBase, TABLES } from './airtable.js';

// Extended table names for materials management
const MATERIALS_TABLES = {
  ...TABLES,
  MATERIAL_VERSIONS: 'Material_Versions',
  MATERIAL_FILES: 'Material_Files',
  MATERIAL_ACCESS_LOGS: 'Material_Access_Logs',
  MATERIAL_BULK_OPERATIONS: 'Material_Bulk_Operations'
};

// Helper function to track materials API calls (following existing pattern)
const trackMaterialsApiCall = async (operation, data, apiCall) => {
  const startTime = performance.now();
  
  try {
    Sentry.addBreadcrumb({
      message: `Materials API: ${operation}`,
      category: 'materials_api',
      level: 'info',
      data: data
    });

    const result = await apiCall();
    const duration = performance.now() - startTime;
    
    Sentry.addBreadcrumb({
      message: `Materials API ${operation} completed`,
      category: 'materials_api',
      level: 'info',
      data: { duration: Math.round(duration), success: true }
    });
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    Sentry.withScope((scope) => {
      scope.setTag('materialsApiOperation', operation);
      scope.setContext('materialsApiCall', {
        operation,
        duration: Math.round(duration),
        data: data
      });
      Sentry.captureException(error);
    });
    
    throw error;
  }
};

/**
 * Material Versions Operations
 */

// Fetch all versions for a session template
const fetchVersionsForSession = async (sessionId) => {
  return trackMaterialsApiCall(
    'fetchVersionsForSession',
    { sessionId },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{session_template} = "${sessionId}"`,
          sort: [{ field: 'created_at', direction: 'desc' }]
        })
        .all();

      return records.map(record => ({
        id: record.id,
        version_id: record.get('version_id'),
        session_template: record.get('session_template'),
        program: record.get('program'),
        created_at: record.get('created_at'),
        created_by: record.get('created_by'),
        change_notes: record.get('change_notes'),
        materials_manifest: JSON.parse(record.get('materials_manifest') || '[]'),
        cdn_urls_list: JSON.parse(record.get('cdn_urls_list') || '[]'),
        materials_count: record.get('materials_count') || 0,
        total_size_bytes: record.get('total_size_bytes') || 0,
        r2_storage_paths: JSON.parse(record.get('r2_storage_paths') || '[]'),
        status: record.get('status'),
        is_master_current: record.get('is_master_current'),
        usage_count: record.get('usage_count') || 0,
        rollback_available: record.get('rollback_available')
      }));
    }
  );
};

// Get the latest master version for a session
const getLatestMasterVersion = async (sessionId) => {
  return trackMaterialsApiCall(
    'getLatestMasterVersion',
    { sessionId },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `AND({session_template} = "${sessionId}", {is_master_current} = TRUE())`,
          maxRecords: 1
        })
        .all();

      if (records.length === 0) {
        return null;
      }

      const record = records[0];
      return {
        id: record.id,
        version_id: record.get('version_id'),
        session_template: record.get('session_template'),
        created_at: record.get('created_at'),
        materials_manifest: JSON.parse(record.get('materials_manifest') || '[]'),
        cdn_urls_list: JSON.parse(record.get('cdn_urls_list') || '[]'),
        materials_count: record.get('materials_count') || 0,
        total_size_bytes: record.get('total_size_bytes') || 0
      };
    }
  );
};

// Get locked version for a specific SO
const getLockedVersionForSO = async (sessionId, soId) => {
  return trackMaterialsApiCall(
    'getLockedVersionForSO',
    { sessionId, soId },
    async () => {
      // First, get the SO's locked version info from Sessions table
      const sessionRecords = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `AND({Session ID} = "${sessionId}", FIND("${soId}", {SOID}))`,
          fields: ['so_materials_version', 'so_materials_cdn_urls'],
          maxRecords: 1
        })
        .all();

      if (sessionRecords.length === 0) {
        return null;
      }

      const sessionRecord = sessionRecords[0];
      const lockedVersionId = sessionRecord.get('so_materials_version');

      if (!lockedVersionId) {
        // If no locked version, return latest master
        return await getLatestMasterVersion(sessionId);
      }

      // Get the specific locked version
      const versionRecords = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{version_id} = "${lockedVersionId}"`,
          maxRecords: 1
        })
        .all();

      if (versionRecords.length === 0) {
        return null;
      }

      const record = versionRecords[0];
      return {
        id: record.id,
        version_id: record.get('version_id'),
        session_template: record.get('session_template'),
        created_at: record.get('created_at'),
        materials_manifest: JSON.parse(record.get('materials_manifest') || '[]'),
        cdn_urls_list: JSON.parse(record.get('cdn_urls_list') || '[]'),
        materials_count: record.get('materials_count') || 0,
        total_size_bytes: record.get('total_size_bytes') || 0
      };
    }
  );
};

// Create a new version record
const createVersionRecord = async (sessionId, versionId, files, changeNotes, adminUser, changeAnalysis) => {
  return trackMaterialsApiCall(
    'createVersionRecord',
    { sessionId, versionId, filesCount: files.length, adminUser },
    async () => {
      // First, mark previous master versions as non-current
      const currentMasterRecords = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `AND({session_template} = "${sessionId}", {is_master_current} = TRUE())`
        })
        .all();

      // Update previous masters to not current
      if (currentMasterRecords.length > 0) {
        const updates = currentMasterRecords.map(record => ({
          id: record.id,
          fields: {
            'is_master_current': false
          }
        }));

        await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS).update(updates);
      }

      // Prepare materials manifest and CDN URLs
      const materialsManifest = files.map(file => ({
        file_uuid: file.file_uuid,
        original_filename: file.original_filename,
        display_name: file.display_name,
        file_size_bytes: file.file_size_bytes,
        content_type: file.content_type,
        cdn_url: file.cdn_url,
        short_code: file.short_code,
        short_link_url: file.short_link_url
      }));

      const cdnUrls = files.map(file => file.cdn_url);
      const r2Paths = files.map(file => file.r2_object_key);
      const totalSize = files.reduce((sum, file) => sum + (file.file_size_bytes || 0), 0);

      // Create the new version record
      const newRecord = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS).create({
        'version_id': versionId,
        'session_template': [sessionId],
        'created_at': new Date().toISOString(),
        'created_by': adminUser,
        'change_notes': changeNotes,
        'materials_manifest': JSON.stringify(materialsManifest),
        'cdn_urls_list': JSON.stringify(cdnUrls),
        'materials_count': files.length,
        'total_size_bytes': totalSize,
        'r2_storage_paths': JSON.stringify(r2Paths),
        'status': 'Active',
        'is_master_current': true,
        'usage_count': 0,
        'rollback_available': true
      });

      // Update the Sessions table with new master version info
      await operationsBase(TABLES.SESSIONS).update(sessionId, {
        'master_materials_manifest': JSON.stringify(materialsManifest),
        'master_materials_cdn_urls': JSON.stringify(cdnUrls),
        'master_materials_version': versionId,
        'materials_last_updated': new Date().toISOString(),
        'materials_updated_by': adminUser
      });

      return {
        id: newRecord.id,
        version_id: newRecord.get('version_id'),
        created_at: newRecord.get('created_at'),
        materials_count: newRecord.get('materials_count'),
        total_size_bytes: newRecord.get('total_size_bytes'),
        changeAnalysis
      };
    }
  );
};

// Lock version for a specific SO
const lockVersionForSO = async (soId, versionId, sessionId) => {
  return trackMaterialsApiCall(
    'lockVersionForSO',
    { soId, versionId, sessionId },
    async () => {
      // Get the version details
      const versionRecord = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{version_id} = "${versionId}"`,
          maxRecords: 1
        })
        .all();

      if (versionRecord.length === 0) {
        throw new Error(`Version ${versionId} not found`);
      }

      const version = versionRecord[0];
      const cdnUrls = JSON.parse(version.get('cdn_urls_list') || '[]');
      const materialsManifest = JSON.parse(version.get('materials_manifest') || '[]');

      // Update the Sessions table for this specific SO
      const sessionRecords = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `AND({Session ID} = "${sessionId}", FIND("${soId}", {SOID}))`
        })
        .all();

      if (sessionRecords.length > 0) {
        const updates = sessionRecords.map(record => ({
          id: record.id,
          fields: {
            'so_materials_version': versionId,
            'so_materials_cdn_urls': JSON.stringify(cdnUrls)
          }
        }));

        await operationsBase(TABLES.SESSIONS).update(updates);
      }

      // Update the Scheduled Offerings table
      const soRecords = await operationsBase(TABLES.SCHEDULED_OFFERINGS)
        .select({
          filterByFormula: `{SO} = "${soId}"`,
          maxRecords: 1
        })
        .all();

      if (soRecords.length > 0) {
        await operationsBase(TABLES.SCHEDULED_OFFERINGS).update(soRecords[0].id, {
          'materials_inherited_at': new Date().toISOString(),
          'materials_locked_version': versionId,
          'materials_locked_cdn_urls': JSON.stringify(cdnUrls),
          'has_custom_materials': false,
          'materials_cdn_manifest': JSON.stringify(materialsManifest)
        });
      }

      // Increment usage count for this version
      const currentUsage = version.get('usage_count') || 0;
      await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS).update(version.id, {
        'usage_count': currentUsage + 1
      });

      return {
        success: true,
        soId,
        versionId,
        cdnUrlsCount: cdnUrls.length
      };
    }
  );
};

/**
 * Material Files Operations
 */

// Get files for a specific version
const getVersionFiles = async (versionId) => {
  return trackMaterialsApiCall(
    'getVersionFiles',
    { versionId },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_FILES)
        .select({
          filterByFormula: `{version} = "${versionId}"`,
          sort: [{ field: 'original_filename', direction: 'asc' }]
        })
        .all();

      return records.map(record => ({
        id: record.id,
        file_id: record.get('file_id'),
        file_uuid: record.get('file_uuid'),
        original_filename: record.get('original_filename'),
        display_name: record.get('display_name'),
        file_extension: record.get('file_extension'),
        content_type: record.get('content_type'),
        file_size_bytes: record.get('file_size_bytes'),
        r2_object_key: record.get('r2_object_key'),
        storage_path: record.get('storage_path'),
        cdn_url: record.get('cdn_url'),
        short_code: record.get('short_code'),
        short_link_url: record.get('short_link_url'),
        download_count: record.get('download_count') || 0,
        unique_downloaders: record.get('unique_downloaders') || 0,
        uploaded_at: record.get('uploaded_at'),
        uploaded_by: record.get('uploaded_by'),
        status: record.get('status')
      }));
    }
  );
};

// Create material file records
const createMaterialFileRecords = async (files, versionId, sessionId, adminUser) => {
  return trackMaterialsApiCall(
    'createMaterialFileRecords',
    { filesCount: files.length, versionId, sessionId, adminUser },
    async () => {
      const fileRecords = files.map(file => ({
        fields: {
          'file_uuid': file.file_uuid,
          'original_filename': file.original_filename,
          'display_name': file.display_name || file.original_filename,
          'file_extension': file.file_extension,
          'content_type': file.content_type,
          'file_size_bytes': file.file_size_bytes,
          'r2_object_key': file.r2_object_key,
          'storage_path': file.storage_path,
          'cdn_url': file.cdn_url,
          'short_code': file.short_code,
          'short_link_url': file.short_link_url,
          'version': [versionId],
          'session': [sessionId],
          'download_count': 0,
          'unique_downloaders': 0,
          'uploaded_at': new Date().toISOString(),
          'uploaded_by': adminUser,
          'status': 'Active'
        }
      }));

      // Create records in batches of 10 (Airtable limit)
      const batchSize = 10;
      const createdRecords = [];

      for (let i = 0; i < fileRecords.length; i += batchSize) {
        const batch = fileRecords.slice(i, i + batchSize);
        const batchResults = await operationsBase(MATERIALS_TABLES.MATERIAL_FILES).create(batch);
        createdRecords.push(...batchResults);
      }

      return createdRecords.map(record => ({
        id: record.id,
        file_id: record.get('file_id'),
        file_uuid: record.get('file_uuid'),
        original_filename: record.get('original_filename'),
        cdn_url: record.get('cdn_url'),
        short_code: record.get('short_code')
      }));
    }
  );
};

/**
 * Version Usage Analytics Operations
 */

// Get version usage count
const getVersionUsageCount = async (versionId) => {
  return trackMaterialsApiCall(
    'getVersionUsageCount',
    { versionId },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{version_id} = "${versionId}"`,
          fields: ['usage_count'],
          maxRecords: 1
        })
        .all();

      return records.length > 0 ? (records[0].get('usage_count') || 0) : 0;
    }
  );
};

// Get SOs affected by rollback
const getSOsAffectedByRollback = async (sessionId, targetVersionId) => {
  return trackMaterialsApiCall(
    'getSOsAffectedByRollback',
    { sessionId, targetVersionId },
    async () => {
      // Get all SOs using versions newer than target version
      const sessionRecords = await operationsBase(TABLES.SESSIONS)
        .select({
          filterByFormula: `{Session ID} = "${sessionId}"`,
          fields: ['SOID', 'so_materials_version']
        })
        .all();

      const affectedSOs = [];

      for (const record of sessionRecords) {
        const soVersion = record.get('so_materials_version');
        const soid = record.get('SOID');

        if (soVersion && soVersion !== targetVersionId) {
          // This is a simplified comparison - in practice you'd compare semantic versions
          affectedSOs.push({
            soid: soid,
            currentVersion: soVersion
          });
        }
      }

      return affectedSOs;
    }
  );
};

// Verify version exists
const verifyVersionExists = async (versionId) => {
  return trackMaterialsApiCall(
    'verifyVersionExists',
    { versionId },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{version_id} = "${versionId}"`,
          fields: ['version_id'],
          maxRecords: 1
        })
        .all();

      return records.length > 0;
    }
  );
};

// Get top downloaded files
const getTopDownloadedFiles = async (sessionId, timeRange = '30days', limit = 10) => {
  return trackMaterialsApiCall(
    'getTopDownloadedFiles',
    { sessionId, timeRange, limit },
    async () => {
      // Get all files for the session
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_FILES)
        .select({
          filterByFormula: `{session} = "${sessionId}"`,
          sort: [{ field: 'download_count', direction: 'desc' }],
          maxRecords: limit
        })
        .all();

      return records.map(record => ({
        original_filename: record.get('original_filename'),
        download_count: record.get('download_count') || 0,
        unique_downloaders: record.get('unique_downloaders') || 0,
        cdn_url: record.get('cdn_url'),
        short_link_url: record.get('short_link_url'),
        file_size_bytes: record.get('file_size_bytes')
      }));
    }
  );
};

/**
 * Access Logging Operations
 */

// Log material access
const logMaterialAccess = async (fileId, accessMethod, soContext = null, userIpHash = null, countryCode = null) => {
  return trackMaterialsApiCall(
    'logMaterialAccess',
    { fileId, accessMethod, soContext },
    async () => {
      const accessRecord = await operationsBase(MATERIALS_TABLES.MATERIAL_ACCESS_LOGS).create({
        'accessed_at': new Date().toISOString(),
        'material_file': [fileId],
        'access_method': accessMethod,
        'so_context': soContext,
        'user_ip_hash': userIpHash,
        'country_code': countryCode
      });

      // Update download count on the file record
      const fileRecord = await operationsBase(MATERIALS_TABLES.MATERIAL_FILES).find(fileId);
      const currentCount = fileRecord.get('download_count') || 0;
      
      await operationsBase(MATERIALS_TABLES.MATERIAL_FILES).update(fileId, {
        'download_count': currentCount + 1
      });

      return {
        id: accessRecord.id,
        accessed_at: accessRecord.get('accessed_at')
      };
    }
  );
};

/**
 * Bulk Operations Support
 */

// Create bulk operation record
const createBulkOperationRecord = async (operationType, affectedSessions, operationData, adminUser) => {
  return trackMaterialsApiCall(
    'createBulkOperationRecord',
    { operationType, sessionsCount: affectedSessions.length, adminUser },
    async () => {
      const bulkOperationRecord = await operationsBase(MATERIALS_TABLES.MATERIAL_BULK_OPERATIONS).create({
        'operation_id': `bulk-${Date.now()}`,
        'operation_type': operationType,
        'affected_sessions': affectedSessions,
        'affected_programs': operationData.affectedPrograms || [],
        'started_at': new Date().toISOString(),
        'performed_by': adminUser,
        'status': 'In Progress',
        'total_files_processed': operationData.totalFiles || 0,
        'total_size_bytes': operationData.totalSize || 0,
        'versions_created': 0,
        'sos_affected': operationData.totalSOsAffected || 0,
        'change_notes': operationData.changeNotes || '',
        'version_increment_type': operationData.versionIncrementType || 'patch',
        'can_rollback': true,
        'operation_metadata': JSON.stringify(operationData)
      });

      return {
        id: bulkOperationRecord.id,
        operation_id: bulkOperationRecord.get('operation_id'),
        operation_type: bulkOperationRecord.get('operation_type'),
        started_at: bulkOperationRecord.get('started_at'),
        status: bulkOperationRecord.get('status')
      };
    }
  );
};

// Update bulk operation progress
const updateBulkOperationProgress = async (operationId, progressData) => {
  return trackMaterialsApiCall(
    'updateBulkOperationProgress',
    { operationId, progress: progressData.overallProgress },
    async () => {
      const operationRecords = await operationsBase(MATERIALS_TABLES.MATERIAL_BULK_OPERATIONS)
        .select({
          filterByFormula: `{operation_id} = "${operationId}"`,
          maxRecords: 1
        })
        .all();

      if (operationRecords.length === 0) {
        throw new Error(`Bulk operation ${operationId} not found`);
      }

      const updateFields = {
        'progress_percent': Math.round(progressData.overallProgress || 0),
        'current_step': progressData.currentStep || '',
        'sessions_completed': progressData.sessionsCompleted || 0,
        'files_processed': progressData.filesProcessed || 0
      };

      if (progressData.completed) {
        updateFields['completed_at'] = new Date().toISOString();
        updateFields['status'] = progressData.success ? 'Completed' : 'Failed';
        updateFields['error_message'] = progressData.errorMessage || null;
      }

      await operationsBase(MATERIALS_TABLES.MATERIAL_BULK_OPERATIONS).update(operationRecords[0].id, updateFields);

      return {
        success: true,
        operationId,
        status: updateFields['status'] || 'In Progress'
      };
    }
  );
};

// Get bulk operations history
const getBulkOperationsHistory = async (limit = 50) => {
  return trackMaterialsApiCall(
    'getBulkOperationsHistory',
    { limit },
    async () => {
      const records = await operationsBase(MATERIALS_TABLES.MATERIAL_BULK_OPERATIONS)
        .select({
          sort: [{ field: 'started_at', direction: 'desc' }],
          maxRecords: limit
        })
        .all();

      return records.map(record => ({
        id: record.id,
        operation_id: record.get('operation_id'),
        operation_type: record.get('operation_type'),
        status: record.get('status'),
        started_at: record.get('started_at'),
        completed_at: record.get('completed_at'),
        performed_by: record.get('performed_by'),
        affected_sessions: record.get('affected_sessions') || [],
        affected_programs: record.get('affected_programs') || [],
        total_files_processed: record.get('total_files_processed') || 0,
        total_size_bytes: record.get('total_size_bytes') || 0,
        versions_created: record.get('versions_created') || 0,
        sos_affected: record.get('sos_affected') || 0,
        change_notes: record.get('change_notes'),
        version_increment_type: record.get('version_increment_type'),
        can_rollback: record.get('can_rollback'),
        error_message: record.get('error_message'),
        operation_metadata: record.get('operation_metadata') ? JSON.parse(record.get('operation_metadata')) : {}
      }));
    }
  );
};

// Perform bulk materials update across multiple sessions
const performBulkMaterialsUpdate = async (operationId, affectedSessions, files, changeNotes, adminUser, versionIncrementType = 'patch') => {
  return trackMaterialsApiCall(
    'performBulkMaterialsUpdate',
    { operationId, sessionsCount: affectedSessions.length, filesCount: files.length },
    async () => {
      const results = {
        operationId,
        affectedSessions: [],
        versionsCreated: [],
        totalFilesProcessed: files.length,
        errors: []
      };

      // Process each session
      for (const sessionId of affectedSessions) {
        try {
          // Create new version for this session
          const versionResult = await createVersionRecord(
            sessionId, 
            `v${Date.now()}-bulk`, // Temporary version ID, would be calculated properly
            files, 
            changeNotes, 
            adminUser, 
            { recommendedIncrementType: versionIncrementType }
          );

          results.versionsCreated.push({
            sessionId,
            versionId: versionResult.version_id,
            filesCount: files.length,
            success: true
          });

          results.affectedSessions.push(sessionId);

        } catch (error) {
          console.error(`Failed to update session ${sessionId}:`, error);
          results.errors.push({
            sessionId,
            error: error.message
          });
        }
      }

      // Update bulk operation record with final results
      await updateBulkOperationProgress(operationId, {
        overallProgress: 100,
        completed: true,
        success: results.errors.length === 0,
        sessionsCompleted: results.affectedSessions.length,
        filesProcessed: results.totalFilesProcessed,
        errorMessage: results.errors.length > 0 ? `${results.errors.length} sessions failed` : null
      });

      return results;
    }
  );
};

/**
 * Rollback Operations
 */

// Perform version rollback
const performRollback = async (sessionId, targetVersionId, adminUser, impact) => {
  return trackMaterialsApiCall(
    'performRollback',
    { sessionId, targetVersionId, adminUser },
    async () => {
      // Get the target version details
      const targetVersion = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `{version_id} = "${targetVersionId}"`,
          maxRecords: 1
        })
        .all();

      if (targetVersion.length === 0) {
        throw new Error(`Target version ${targetVersionId} not found`);
      }

      const version = targetVersion[0];
      const materialsManifest = version.get('materials_manifest');
      const cdnUrls = version.get('cdn_urls_list');

      // Mark current master as non-current
      const currentMasterRecords = await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS)
        .select({
          filterByFormula: `AND({session_template} = "${sessionId}", {is_master_current} = TRUE())`
        })
        .all();

      if (currentMasterRecords.length > 0) {
        const updates = currentMasterRecords.map(record => ({
          id: record.id,
          fields: { 'is_master_current': false }
        }));
        await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS).update(updates);
      }

      // Mark target version as current master
      await operationsBase(MATERIALS_TABLES.MATERIAL_VERSIONS).update(version.id, {
        'is_master_current': true
      });

      // Update Sessions table with rollback
      await operationsBase(TABLES.SESSIONS).update(sessionId, {
        'master_materials_manifest': materialsManifest,
        'master_materials_cdn_urls': cdnUrls,
        'master_materials_version': targetVersionId,
        'materials_last_updated': new Date().toISOString(),
        'materials_updated_by': adminUser
      });

      // Log the rollback operation
      await operationsBase(MATERIALS_TABLES.MATERIAL_BULK_OPERATIONS).create({
        'operation_type': 'Rollback',
        'affected_sessions': [sessionId],
        'started_at': new Date().toISOString(),
        'completed_at': new Date().toISOString(),
        'performed_by': adminUser,
        'status': 'Completed',
        'can_rollback': true
      });

      return {
        success: true,
        targetVersionId,
        rollbackedAt: new Date().toISOString()
      };
    }
  );
};

// Export all functions for use in other modules
export {
  MATERIALS_TABLES,
  trackMaterialsApiCall,
  fetchVersionsForSession,
  getLatestMasterVersion,
  getLockedVersionForSO,
  createVersionRecord,
  lockVersionForSO,
  getVersionFiles,
  createMaterialFileRecords,
  getVersionUsageCount,
  getSOsAffectedByRollback,
  verifyVersionExists,
  getTopDownloadedFiles,
  logMaterialAccess,
  performRollback,
  createBulkOperationRecord,
  updateBulkOperationProgress,
  getBulkOperationsHistory,
  performBulkMaterialsUpdate
};